<template>
  <div
    class="ai-variable-editor"
    :class="{
      'ai-variable-editor--compact': compact,
      'ai-variable-editor--sidebar': layout === 'sidebar',
    }"
  >
    <span v-if="showLabel" class="ai-field-label d-block mb-50">{{ label }}</span>
    <p v-if="showTokenHint" class="ai-variable-editor__hint mb-75">
      Each field fills a matching chip in the script.
    </p>
    <div class="ai-variable-editor__fields">
      <div
        v-for="field in visibleFields"
        :key="field.key"
        class="ai-variable-editor__field"
      >
        <label class="ai-sub-label">
          <span>{{ field.label }}</span>
          <span
            class="ai-field-token"
            :title="'Used in the script as {{' + field.key + '}}'"
          >{{ field.chip }}</span>
        </label>
        <b-form-input
          :value="value[field.key]"
          size="sm"
          :disabled="disabled"
          :placeholder="field.placeholder"
          @input="emitField(field.key, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'

const JOB_FIELDS = [
  { key: 'client', label: 'Client Name', placeholder: 'Client name', chip: 'client' },
  { key: 'role', label: 'Position Name', placeholder: 'Role / position', chip: 'role' },
  { key: 'location', label: 'Locations', placeholder: 'Location', chip: 'location' },
  { key: 'industry', label: 'Industry', placeholder: 'Industry / vertical', chip: 'industry' },
  { key: 'agent_name', label: 'Agent', placeholder: 'Calling agent name', chip: 'agent' },
  { key: 'company', label: 'Company', placeholder: 'Recruiter company name', chip: 'company' },
  { key: 'min_experience_years', label: 'Min exp (yrs)', placeholder: 'Minimum experience', chip: 'min exp' },
  { key: 'max_experience_years', label: 'Max exp (yrs)', placeholder: 'Maximum experience', chip: 'max exp' },
  { key: 'min_ctc', label: 'Min CTC (Lacs)', placeholder: 'Minimum CTC', chip: 'min ctc' },
  { key: 'max_ctc', label: 'Max CTC (Lacs)', placeholder: 'Maximum CTC', chip: 'max ctc' },
  { key: 'preferred_qualification', label: 'Qualification', placeholder: 'Preferred qualification', chip: 'qualification' },
]

const DRIVE_FIELDS = [
  { key: 'drive_name', label: 'Drive name', placeholder: 'Recruitment drive name', chip: 'drive' },
  { key: 'drive_dates', label: 'Drive dates', placeholder: 'Drive date(s)', chip: 'dates' },
]

export default {
  name: 'AiCallingVariableEditor',
  components: { BFormInput },
  props: {
    value: {
      type: Object,
      default: () => ({
        client: '',
        role: '',
        location: '',
        industry: '',
        agent_name: '',
        company: '',
        min_experience_years: '',
        max_experience_years: '',
        min_ctc: '',
        max_ctc: '',
        preferred_qualification: '',
        drive_name: '',
        drive_dates: '',
      }),
    },
    disabled: { type: Boolean, default: false },
    showDriveFields: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true },
    label: { type: String, default: 'Job details (editable)' },
    compact: { type: Boolean, default: false },
    layout: { type: String, default: 'grid' },
    showTokenHint: { type: Boolean, default: false },
  },
  computed: {
    visibleFields() {
      return this.showDriveFields ? JOB_FIELDS.concat(DRIVE_FIELDS) : JOB_FIELDS
    },
  },
  methods: {
    emitField(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
  },
}
</script>

<style scoped>
.ai-field-label {
  font-size: 15px;
  font-weight: 700;
  color: #2f2b3d;
}
.ai-variable-editor__hint {
  font-size: 13px;
  line-height: 1.4;
  color: #6e6b7b;
}
.ai-sub-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  font-size: 13px;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.3rem;
  text-transform: none;
  letter-spacing: normal;
}
.ai-field-token {
  display: inline-flex;
  align-items: center;
  padding: 0 0.4em;
  border-radius: 999px;
  background: rgba(0, 172, 153, 0.12);
  border: 1px solid rgba(0, 172, 153, 0.35);
  color: #007a6e;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  flex-shrink: 0;
}
.ai-variable-editor__fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem 0.75rem;
}
.ai-variable-editor__field {
  min-width: 0;
}
.ai-variable-editor--compact .ai-sub-label {
  font-size: 13px;
  margin-bottom: 0.25rem;
}
.ai-variable-editor ::v-deep .form-control-sm {
  min-height: 2.35rem;
  font-size: 14px;
  padding: 0.4rem 0.65rem;
  color: #4b465c;
}
@media (min-width: 576px) {
  .ai-variable-editor:not(.ai-variable-editor--sidebar) .ai-variable-editor__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1400px) {
  .ai-variable-editor:not(.ai-variable-editor--sidebar) .ai-variable-editor__fields {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
